import { ButtonWithIcon } from "@/components/Buttons/ButtonWithIcon"
import { FullLoader } from "@/components/FullLoader"
import { CopyIcon } from "@/components/Icons"
import { H3, P1 } from "@/components/Typographies"
import type { RelatedClusterProperties } from "@/domain/cluster/constants"
import { getDefaultUserCollaborator } from "@/domain/collaborator/getRelatedUserCollaborator"
import { getDefaultRelatedContactDetails } from "@/domain/contactdetails/getContactDetails"
import { NIL_UUID } from "@/domain/resources/constants"
import { getDisplayedName } from "@/domain/user/getDisplayedName"
import useGetUserInfos from "@/hooks/useGetUserInfos"
import {
  type ClusterJsonldReadCollectionCluster,
  type CollaboratorJsonldUserReadItem,
  useGetCluster,
  useGetClusters,
} from "@/networking/api"
import { ADDRESS_FORMAT, formatAddress } from "@/utils/address/formatAddress"
import { isNotBlank, isNotNullNorUndefined, isNullOrUndefined } from "@/utils/ts-utils"
import { Box, Flex, chakra } from "@chakra-ui/react"
import { useMemo, useState } from "react"
import { FormattedMessage } from "react-intl"
import templateMailHouse from "./templates/MailSignHouse"
import templateMailHouse2 from "./templates/MailSignHouse2"
import templateMailMain from "./templates/MailSignMain"
import templateMailMoment from "./templates/MailSignMoment"

// MAISON BARNES New-York
const UUID_HOUSE = "f598e914-74ec-551a-b576-f788bf8dfb61"
// MAISON BOISSIERE
const UUID_HOUSE2 = "e891ce03-e156-5289-9d56-d6739238384f"
// UN MOMENT BARNES
const UUID_MOMENT = "db85f19e-5a01-576d-97b1-a9e6a6cd52a2"

const getUserCollabClusters = <T extends { cluster?: { cluster?: { id: string } | null }[] }>(
  collaborators: T[]
) => {
  const clusters: string[] = []
  collaborators.map(cl => {
    ;(cl.cluster ?? []).map(c => {
      clusters.push(c.cluster?.id ?? "")
    })
  })
  return new Set(clusters)
}

const parseAndReplace = (content: string, values: { from: string; to: string }[]) => {
  let out = content
  values.map(({ from, to }) => {
    out = out.replace(new RegExp(`\{\{${from}\}\}`, "igm"), to)
  })
  return out
}

const getClusterWithRelUserInfos = (
  cluster?: ClusterJsonldReadCollectionCluster,
  collaborator?: CollaboratorJsonldUserReadItem
): (ClusterJsonldReadCollectionCluster & RelatedClusterProperties) | undefined => {
  if (isNullOrUndefined(cluster)) return undefined

  const newFields = {
    function: null,
    rank: null,
    role: null,
    isDefault: null,
    isPublished: null,
    isPublishedInDirectory: null,
  } as RelatedClusterProperties

  if (isNullOrUndefined(collaborator)) return { ...cluster, ...newFields }

  const relCluster = (collaborator?.cluster ?? []).filter(rel => rel.cluster?.id === cluster.id)[0]

  if (isNullOrUndefined(relCluster)) return { ...cluster, ...newFields }

  newFields.function = relCluster.function
  newFields.rank = relCluster.rank ?? undefined
  newFields.role = relCluster.role ?? undefined
  newFields.isDefault = relCluster.isDefault ?? false
  newFields.isPublished = relCluster.isPublished ?? false
  newFields.isPublishedInDirectory = relCluster.isPublishedInDirectory ?? false

  return { ...cluster, ...newFields }
}

const prepareReplacements = (
  collaborator?: CollaboratorJsonldUserReadItem,
  cluster?: ClusterJsonldReadCollectionCluster & RelatedClusterProperties
) => {
  const colContactDetails = getDefaultRelatedContactDetails(collaborator?.contactDetails)
  const cluContactDetails = getDefaultRelatedContactDetails(cluster?.contactDetails)
  return [
    { from: "collaborator", to: getDisplayedName(collaborator?.firstname, collaborator?.lastname) },
    { from: "function", to: cluster?.function ?? "" },
    {
      from: "phone",
      to:
        colContactDetails?.mobile ??
        colContactDetails?.mobile2 ??
        colContactDetails?.phone ??
        colContactDetails?.phone2 ??
        colContactDetails?.phoneWww ??
        cluContactDetails?.phone ??
        cluContactDetails?.phone2 ??
        cluContactDetails?.phoneWww ??
        "",
    },
    {
      from: "email",
      to:
        colContactDetails?.email ??
        colContactDetails?.emailFallback ??
        cluContactDetails?.email ??
        cluContactDetails?.emailFallback ??
        "",
    },
    { from: "clusterName", to: cluster?.name ?? "BARNES International" },
    {
      from: "clusterAddress",
      to: formatAddress(cluContactDetails, ADDRESS_FORMAT.FULL),
    },
  ]
}

const toBase64 = (str: string) => {
  const bytes = new TextEncoder().encode(str)
  return btoa(Array.from(bytes, byte => String.fromCharCode(byte)).join(""))
}

// The preview body has no margin so the 800px signature fits the 810px frame without scrollbars.
export const toPreviewSrc = (content: string) =>
  `data:text/html;base64,${toBase64(`<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{margin:0}</style></head><body>${content}</body></html>`)}`

const copy = async (content?: string) => {
  const ct = content ?? ""
  const html = new Blob([ct], { type: "text/html" })
  const itemHtml = new ClipboardItem({ "text/html": html })

  try {
    await navigator.clipboard.write([itemHtml])
  } catch (e) {
    console.log("Automatic copy not available or not allowed", e)
  }
}

export const MailSign: React.FC = () => {
  let copyLabelItv1: ReturnType<typeof setTimeout>
  let copyLabelItv2: ReturnType<typeof setTimeout>
  let copyLabelItv3: ReturnType<typeof setTimeout>
  let copyLabelItv4: ReturnType<typeof setTimeout>
  const [copyLabel1, setCopyLabel1] = useState<string>("")
  const [copyLabel2, setCopyLabel2] = useState<string>("")
  const [copyLabel3, setCopyLabel3] = useState<string>("")
  const [copyLabel4, setCopyLabel4] = useState<string>("")

  const { data: customClusters } = useGetClusters({
    "id[]": [UUID_HOUSE, UUID_HOUSE2, UUID_MOMENT],
  })

  const { data: user, defaultCluster } = useGetUserInfos()
  const collaborator = getDefaultUserCollaborator(user?.collaborators)
  // Only once the default agency is known: asked for the nil id, the API answers 404 and the
  // query retried three times (1s, 2s, 4s) behind the full loader, about 8s.
  const { data: cluster, isInitialLoading: isClusterLoading } = useGetCluster(
    defaultCluster?.id ?? NIL_UUID,
    {
      withAgencies: false,
      withCollaborators: false,
      withPeople: false,
      withProjects: false,
    },
    { query: { enabled: isNotNullNorUndefined(defaultCluster) } }
  )
  const [clusterHouse, clusterHouse2, clusterMoment] = useMemo(() => {
    const clusters = customClusters?.["hydra:member"] ?? []
    const c1 = clusters.filter(c => c.id === UUID_HOUSE)[0] ?? undefined
    const c2 = clusters.filter(c => c.id === UUID_HOUSE2)[0] ?? undefined
    const c3 = clusters.filter(c => c.id === UUID_MOMENT)[0] ?? undefined
    return [
      getClusterWithRelUserInfos(c1, collaborator),
      getClusterWithRelUserInfos(c2, collaborator),
      getClusterWithRelUserInfos(c3, collaborator),
    ]
  }, [customClusters, collaborator])

  const mail_1 = useMemo(
    () =>
      parseAndReplace(
        templateMailMain,
        prepareReplacements(collaborator, {
          ...defaultCluster,
          ...cluster,
        } as (ClusterJsonldReadCollectionCluster & RelatedClusterProperties) | undefined)
      ),
    [collaborator, cluster, defaultCluster]
  )
  const mail_2 = useMemo(
    () => parseAndReplace(templateMailHouse, prepareReplacements(collaborator, clusterHouse)),
    [collaborator, clusterHouse]
  )
  const mail_3 = useMemo(
    () => parseAndReplace(templateMailMoment, prepareReplacements(collaborator, clusterMoment)),
    [collaborator, clusterMoment]
  )
  const mail_4 = useMemo(
    () => parseAndReplace(templateMailHouse2, prepareReplacements(collaborator, clusterHouse2)),
    [collaborator, clusterHouse2]
  )

  const userClusters = getUserCollabClusters(user?.collaborators ?? [])

  const isLinkedToMoment = userClusters.has(UUID_MOMENT)
  const isLinkedToHouse = userClusters.has(UUID_HOUSE)
  const isLinkedToHouse2 = userClusters.has(UUID_HOUSE2)

  return (
    <>
      <Flex flexDir="column" gap="6" px="8" py="12">
        <H3>
          <FormattedMessage id="resources.mail_sign" />
        </H3>
      </Flex>

      {isClusterLoading ? (
        <FullLoader />
      ) : (
        <Flex flexDir="column" gap="6" px="8" py="12">
          <P1 pb={5}>
            <FormattedMessage id="resources.mail_sign.description" />
          </P1>
          {/** Model #1 **/}
          <Flex py={2} alignSelf="center" flexShrink={0} flexGrow={1} flexDir="row" w="100%">
            <Box
              position="relative"
              display="block"
              border="solid 1px"
              borderColor="grey.200"
              style={{ containerType: "size" }}
              w="810px"
              minH="196px"
              resize="vertical"
              overflow="auto"
            >
              <chakra.iframe title="#1" w="100%" h="100%" src={toPreviewSrc(mail_1)} bg="white" />
              <ButtonWithIcon
                variant="solid"
                position="absolute"
                left="5px"
                top="5px"
                paddingInlineStart={2}
                paddingInlineEnd={2}
                size="sm"
                onClick={() => {
                  copy(mail_1)
                  setCopyLabel1("copied")
                  clearTimeout(copyLabelItv1)
                  copyLabelItv1 = setTimeout(() => setCopyLabel1(""), 3000)
                }}
                icon={CopyIcon}
              >
                {isNotBlank(copyLabel1) && <FormattedMessage id={copyLabel1} />}
              </ButtonWithIcon>
            </Box>
          </Flex>
          {
            /** Model #2 **/
            isLinkedToHouse && (
              <Flex py={2} alignSelf="center" flexShrink={0} flexGrow={1} flexDir="row" w="100%">
                <Box
                  position="relative"
                  display="block"
                  border="solid 1px"
                  borderColor="grey.200"
                  w="810px"
                  style={{ containerType: "size" }}
                  minH="196px"
                  resize="vertical"
                  overflow="auto"
                >
                  <chakra.iframe
                    title="#1"
                    w="100%"
                    h="100%"
                    src={toPreviewSrc(mail_2)}
                    bg="white"
                  />
                  <ButtonWithIcon
                    variant="solid"
                    position="absolute"
                    left="5px"
                    top="5px"
                    paddingInlineStart={2}
                    paddingInlineEnd={2}
                    size="sm"
                    onClick={() => {
                      copy(mail_2)
                      setCopyLabel2("copied")
                      clearTimeout(copyLabelItv2)
                      copyLabelItv2 = setTimeout(() => setCopyLabel2(""), 3000)
                    }}
                    icon={CopyIcon}
                  >
                    {isNotBlank(copyLabel2) && <FormattedMessage id={copyLabel2} />}
                  </ButtonWithIcon>
                </Box>
              </Flex>
            )
          }
          {
            /** Model #3 **/
            isLinkedToMoment && (
              <Flex py={2} alignSelf="center" flexShrink={0} flexGrow={1} flexDir="row" w="100%">
                <Box
                  position="relative"
                  display="block"
                  border="solid 1px"
                  borderColor="grey.200"
                  w="810px"
                  style={{ containerType: "size" }}
                  minH="196px"
                  resize="vertical"
                  overflow="auto"
                >
                  <chakra.iframe
                    title="#1"
                    w="100%"
                    h="100%"
                    src={toPreviewSrc(mail_3)}
                    bg="white"
                  />
                  <ButtonWithIcon
                    variant="solid"
                    position="absolute"
                    left="5px"
                    top="5px"
                    paddingInlineStart={2}
                    paddingInlineEnd={2}
                    size="sm"
                    onClick={() => {
                      copy(mail_3)
                      setCopyLabel3("copied")
                      clearTimeout(copyLabelItv3)
                      copyLabelItv3 = setTimeout(() => setCopyLabel3(""), 3000)
                    }}
                    icon={CopyIcon}
                  >
                    {isNotBlank(copyLabel3) && <FormattedMessage id={copyLabel3} />}
                  </ButtonWithIcon>
                </Box>
              </Flex>
            )
          }
          {
            /** Model #4 **/
            isLinkedToHouse2 && (
              <Flex py={2} alignSelf="center" flexShrink={0} flexGrow={1} flexDir="row" w="100%">
                <Box
                  position="relative"
                  display="block"
                  border="solid 1px"
                  borderColor="grey.200"
                  w="810px"
                  style={{ containerType: "size" }}
                  minH="196px"
                  resize="vertical"
                  overflow="auto"
                >
                  <chakra.iframe
                    title="#1"
                    w="100%"
                    h="100%"
                    src={toPreviewSrc(mail_4)}
                    bg="white"
                  />
                  <ButtonWithIcon
                    variant="solid"
                    position="absolute"
                    left="5px"
                    top="5px"
                    paddingInlineStart={2}
                    paddingInlineEnd={2}
                    size="sm"
                    onClick={() => {
                      copy(mail_4)
                      setCopyLabel4("copied")
                      clearTimeout(copyLabelItv4)
                      copyLabelItv4 = setTimeout(() => setCopyLabel4(""), 3000)
                    }}
                    icon={CopyIcon}
                  >
                    {isNotBlank(copyLabel4) && <FormattedMessage id={copyLabel4} />}
                  </ButtonWithIcon>
                </Box>
              </Flex>
            )
          }
        </Flex>
      )}
    </>
  )
}
